import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Calendar,
  CheckSquare,
  Clock,
  AlertCircle,
  Loader2,
  GripVertical,
  User,
  Flag,
  FolderKanban,
  X,
  Pencil
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO, isPast, isToday } from 'date-fns';

const STATUS_CONFIG = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700' },
  in_review: { label: 'In Review', color: 'bg-purple-50 text-purple-700' },
  blocked: { label: 'Blocked', color: 'bg-red-50 text-red-700' },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', color: 'bg-blue-50 text-blue-600' },
  high: { label: 'High', color: 'bg-amber-50 text-amber-600' },
  critical: { label: 'Critical', color: 'bg-red-50 text-red-600' }
};

const PRIORITY_COLORS = {
  low: '#6b7280',
  medium: '#3b82f6',
  high: '#f59e0b',
  critical: '#ef4444'
};

// Task Detail Modal Component
const TaskDetailModal = ({ task, users, projects, open, onClose, onEdit }) => {
  if (!task) return null;
  
  const assignees = users?.filter(u => task.assigned_to?.includes(u.id)) || [];
  const project = projects?.find(p => p.id === task.project_id);
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.backlog;
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  const isOverdue = task.due_date && isPast(parseISO(task.due_date)) && task.status !== 'completed';
  
  const completedSubtasks = task.subtasks?.filter(s => s.completed)?.length || 0;
  const totalSubtasks = task.subtasks?.length || 0;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="task-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CheckSquare className="w-5 h-5 text-slate-400" />
              Task Details
            </DialogTitle>
            {onEdit && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { onClose(false); onEdit(task); }}
                data-testid="task-detail-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Task Name & Description */}
          <div>
            <h3 className="font-semibold text-slate-900 text-base" data-testid="task-detail-name">
              {task.name}
            </h3>
            {task.description && (
              <p className="text-sm text-slate-500 mt-1" data-testid="task-detail-description">
                {task.description}
              </p>
            )}
          </div>
          
          {/* Status & Priority Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={statusConfig.color} data-testid="task-detail-status">
              {statusConfig.label}
            </Badge>
            <Badge 
              style={{ backgroundColor: `${priorityColor}20`, color: priorityColor }}
              data-testid="task-detail-priority"
            >
              <Flag className="w-3 h-3 mr-1" />
              {priorityConfig.label}
            </Badge>
            {isOverdue && (
              <Badge className="bg-red-100 text-red-700">
                <AlertCircle className="w-3 h-3 mr-1" />
                Overdue
              </Badge>
            )}
          </div>
          
          {/* Task Info Grid */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            {/* Project */}
            {project && (
              <div className="flex items-start gap-2">
                <FolderKanban className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Project</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="task-detail-project">
                    {project.name}
                  </p>
                </div>
              </div>
            )}
            
            {/* Assignees */}
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Assigned To</p>
                <div className="flex items-center gap-1" data-testid="task-detail-assignees">
                  {assignees.length > 0 ? (
                    <div className="flex -space-x-1">
                      {assignees.slice(0, 3).map(user => (
                        <Avatar key={user.id} className="h-6 w-6 border-2 border-white">
                          <AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">
                            {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      {assignees.length > 3 && (
                        <span className="text-xs text-slate-500 ml-1">+{assignees.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-slate-400">Unassigned</span>
                  )}
                </div>
                {assignees.length > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {assignees.map(u => u.name).join(', ')}
                  </p>
                )}
              </div>
            </div>
            
            {/* Start Date */}
            {task.start_date && (
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Start Date</p>
                  <p className="text-sm font-medium text-slate-700">
                    {format(parseISO(task.start_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            )}
            
            {/* Due Date */}
            {task.due_date && (
              <div className="flex items-start gap-2">
                <Clock className={`w-4 h-4 mt-0.5 ${isOverdue ? 'text-red-500' : 'text-slate-400'}`} />
                <div>
                  <p className="text-xs text-slate-500">Due Date</p>
                  <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-slate-700'}`} data-testid="task-detail-due-date">
                    {format(parseISO(task.due_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            )}
            
            {/* Estimated Hours */}
            {task.estimated_hours && (
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Estimated</p>
                  <p className="text-sm font-medium text-slate-700">
                    {task.estimated_hours} hours
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Subtasks */}
          {totalSubtasks > 0 && (
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700">Subtasks</p>
                <span className="text-xs text-slate-500">
                  {completedSubtasks}/{totalSubtasks} completed
                </span>
              </div>
              <div className="space-y-2">
                {task.subtasks.map(subtask => (
                  <div 
                    key={subtask.id} 
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                  >
                    <CheckSquare 
                      className={`w-4 h-4 ${subtask.completed ? 'text-emerald-500' : 'text-slate-300'}`} 
                    />
                    <span className={`text-sm ${subtask.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {subtask.text}
                    </span>
                  </div>
                ))}
              </div>
              {/* Progress Bar */}
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${(completedSubtasks / totalSubtasks) * 100}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Close Button */}
          <div className="pt-4 border-t border-slate-100">
            <Button 
              variant="outline" 
              onClick={() => onClose(false)} 
              className="w-full"
              data-testid="task-detail-close-btn"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TaskCard = ({ task, users, projects, onEdit, onDelete, onStatusChange, onClick, isDragging, canDrag = true, canEdit = true, canDelete = true }) => {
  const assignees = users.filter(u => task.assigned_to?.includes(u.id));
  const project = projects.find(p => p.id === task.project_id);
  const isOverdue = task.due_date && isPast(parseISO(task.due_date)) && task.status !== 'completed';
  const isDueToday = task.due_date && isToday(parseISO(task.due_date));
  const [dragging, setDragging] = useState(false);

  const handleDragStart = (e) => {
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    setDragging(true);
    e.dataTransfer.setData('taskId', task.id);
    e.dataTransfer.setData('sourceStatus', task.status);
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay for visual effect
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setDragging(false);
    e.target.style.opacity = '1';
  };

  const handleClick = (e) => {
    // Don't trigger click if dragging or clicking on dropdown
    if (dragging) return;
    onClick?.(task);
  };

  return (
    <div
      className={`kanban-card group transition-all duration-200 ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${dragging ? 'ring-2 ring-blue-500 ring-opacity-50 scale-[1.02] shadow-lg' : ''} hover:shadow-md`}
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      data-testid={`task-card-${task.id}`}
    >
      {/* Drag Handle - only show if can drag */}
      {canDrag && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-50 transition-opacity">
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
      )}
      
      <div className="flex items-start justify-between mb-2">
        <Badge className={PRIORITY_CONFIG[task.priority]?.color} variant="outline">
          {PRIORITY_CONFIG[task.priority]?.label}
        </Badge>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                <MoreVertical className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(task); }}>Edit</DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(task.id); }} className="text-red-600">Delete</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      
      <h4 className="font-medium text-sm text-slate-900 mb-1">{task.name}</h4>
      {task.description && (
        <p className="text-xs text-slate-500 line-clamp-2 mb-3">{task.description}</p>
      )}
      
      {project && (
        <div className="text-xs text-slate-400 mb-2">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            {project.name}
          </span>
        </div>
      )}
      
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <div className="flex -space-x-2">
          {assignees.slice(0, 3).map(user => (
            <Avatar key={user.id} className="h-6 w-6 border-2 border-white">
              <AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">
                {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>
          ))}
          {assignees.length > 3 && (
            <div className="h-6 w-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] text-slate-600">
              +{assignees.length - 3}
            </div>
          )}
        </div>
        
        {task.due_date && (
          <div className={`flex items-center gap-1 text-xs ${
            isOverdue ? 'text-red-600' : isDueToday ? 'text-amber-600' : 'text-slate-400'
          }`}>
            {isOverdue && <AlertCircle className="w-3 h-3" />}
            <Calendar className="w-3 h-3" />
            <span>{format(parseISO(task.due_date), 'MMM d')}</span>
          </div>
        )}
      </div>
      
      {task.subtasks?.length > 0 && (
        <div className="mt-2 text-xs text-slate-400">
          <CheckSquare className="w-3 h-3 inline mr-1" />
          {task.subtasks.filter(s => s.completed).length}/{task.subtasks.length} subtasks
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({ status, tasks, users, projects, onEdit, onDelete, onDrop, onClick, canDrag = true, canEdit = true, canDelete = true }) => {
  const statusConfig = STATUS_CONFIG[status];
  const columnTasks = tasks.filter(t => t.status === status);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const handleDragOver = (e) => {
    if (!canDrag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    // Only set to false if we're leaving the column, not entering a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const taskId = e.dataTransfer.getData('taskId');
    const sourceStatus = e.dataTransfer.getData('sourceStatus');
    if (taskId && sourceStatus !== status) {
      onDrop(taskId, status);
    }
  };
  
  return (
    <div 
      className={`kanban-column transition-all duration-200 ${
        isDragOver ? 'bg-blue-50/50 ring-2 ring-blue-300 ring-inset' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`task-column-${status}`}
    >
      <div className="kanban-column-header">
        <span className="kanban-column-title">{statusConfig?.label || status}</span>
        <span className={`kanban-column-count ${isDragOver ? 'bg-blue-200 text-blue-700' : ''}`}>
          {columnTasks.length}
        </span>
      </div>
      
      {/* Drop Zone Indicator */}
      {isDragOver && columnTasks.length === 0 && (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-blue-300 rounded-lg m-2 p-4">
          <p className="text-sm text-blue-500">Drop task here</p>
        </div>
      )}
      
      <ScrollArea className="flex-1 pr-2">
        <div className="space-y-5 pb-2">
          {columnTasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              users={users}
              projects={projects}
              onEdit={onEdit}
              onDelete={onDelete}
              onClick={onClick}
              canDrag={canDrag}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export const TasksPage = () => {
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const { can, canEdit } = usePermissions();
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter');
  
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(urlFilter === 'blocked' ? 'blocked' : 'all');
  const [specialFilter, setSpecialFilter] = useState(urlFilter === 'overdue' ? 'overdue' : 'none');
  const [projectFilter, setProjectFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Task Detail Modal state
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assigned_to: [],
    project_id: '',
    priority: 'medium',
    status: 'backlog',
    due_date: '',
    start_date: '',
    estimated_hours: '',
    subtasks: []
  });

  const [newSubtask, setNewSubtask] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, usersRes, projectsRes] = await Promise.all([
        api.get('/tasks'),
        api.get('/users'),
        api.get('/projects')
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates - efficient handlers for task changes
  useEffect(() => {
    const unsubscribes = [];
    
    // Handle task creation
    unsubscribes.push(subscribe('task_created', (message) => {
      if (message.data) {
        setTasks(prev => {
          if (prev.some(t => t.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
      }
    }));
    
    // Handle task updates
    unsubscribes.push(subscribe('task_updated', (message) => {
      if (message.data) {
        setTasks(prev => prev.map(t => 
          t.id === message.data.id ? { ...t, ...message.data } : t
        ));
      }
    }));
    
    // Handle task deletion
    unsubscribes.push(subscribe('task_deleted', (message) => {
      if (message.data?.id) {
        setTasks(prev => prev.filter(t => t.id !== message.data.id));
      }
    }));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        project_id: formData.project_id || null
      };
      
      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, payload);
      } else {
        await api.post('/tasks', payload);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save task:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      description: task.description,
      assigned_to: task.assigned_to || [],
      project_id: task.project_id || '',
      priority: task.priority,
      status: task.status,
      due_date: task.due_date || '',
      start_date: task.start_date || '',
      estimated_hours: task.estimated_hours?.toString() || '',
      subtasks: task.subtasks || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  // Handle task click to show detail modal
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailModalOpen(true);
  };

  const addSubtask = () => {
    if (!newSubtask.trim()) return;
    setFormData({
      ...formData,
      subtasks: [...formData.subtasks, { id: Date.now().toString(), text: newSubtask, completed: false }]
    });
    setNewSubtask('');
  };

  const removeSubtask = (id) => {
    setFormData({
      ...formData,
      subtasks: formData.subtasks.filter(s => s.id !== id)
    });
  };

  const toggleSubtask = (id) => {
    setFormData({
      ...formData,
      subtasks: formData.subtasks.map(s => 
        s.id === id ? { ...s, completed: !s.completed } : s
      )
    });
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      name: '',
      description: '',
      assigned_to: [],
      project_id: '',
      priority: 'medium',
      status: 'backlog',
      due_date: '',
      start_date: '',
      estimated_hours: '',
      subtasks: []
    });
    setNewSubtask('');
  };

  const filteredTasks = tasks.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchesProject = projectFilter === 'all' || t.project_id === projectFilter;
    
    // Special filter for overdue tasks
    if (specialFilter === 'overdue') {
      const isOverdue = t.due_date && isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date)) && t.status !== 'completed';
      return matchesSearch && matchesProject && isOverdue;
    }
    
    return matchesSearch && matchesStatus && matchesProject;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Tasks</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">{tasks.length} total tasks</p>
        </div>
        
        {can('task:create') && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="new-task-btn">
                <Plus className="w-4 h-4" />
                New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Task Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Write blog post draft"
                    required
                    data-testid="task-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the task..."
                    rows={3}
                  data-testid="task-description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({...formData, project_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="task-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select 
                  value={formData.assigned_to[0] || "unassigned"} 
                  onValueChange={(v) => setFormData({...formData, assigned_to: v === "unassigned" ? [] : [v]})}
                >
                  <SelectTrigger data-testid="task-assignee-select">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger data-testid="task-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="task-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    data-testid="task-due-date"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Estimated Hours</Label>
                <Input
                  type="number"
                  value={formData.estimated_hours}
                  onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})}
                  placeholder="4"
                  min="0"
                  step="0.5"
                  data-testid="task-estimated-hours"
                />
              </div>
              
              {/* Subtasks */}
              <div className="space-y-2">
                <Label>Subtasks</Label>
                <div className="flex gap-2">
                  <Input
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    placeholder="Add a subtask"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtask())}
                    data-testid="subtask-input"
                  />
                  <Button type="button" variant="outline" onClick={addSubtask} data-testid="add-subtask-btn">
                    Add
                  </Button>
                </div>
                {formData.subtasks.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {formData.subtasks.map(subtask => (
                      <div key={subtask.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={subtask.completed}
                          onChange={() => toggleSubtask(subtask.id)}
                          className="rounded"
                        />
                        <span className={`flex-1 text-sm ${subtask.completed ? 'line-through text-slate-400' : ''}`}>
                          {subtask.text}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSubtask(subtask.id)}
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-task-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingTask ? 'Update' : 'Create'} Task
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-1 sm:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 w-full sm:w-64"
            data-testid="search-tasks"
          />
        </div>
        <div className="flex gap-3 flex-1 sm:flex-none">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 sm:w-40" data-testid="filter-task-status">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="flex-1 sm:w-48" data-testid="filter-task-project">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Kanban Board - Horizontal scroll on mobile */}
      <div className="kanban-board overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
        {Object.keys(STATUS_CONFIG).map(status => (
          <KanbanColumn
            key={status}
            status={status}
            tasks={filteredTasks}
            users={users}
            projects={projects}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDrop={handleStatusChange}
            onClick={handleTaskClick}
            canDrag={can('task:update')}
            canEdit={canEdit()}
            canDelete={can('task:delete')}
          />
        ))}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        users={users}
        projects={projects}
        open={detailModalOpen}
        onClose={setDetailModalOpen}
        onEdit={canEdit() ? handleEdit : null}
      />
    </div>
  );
};
